/// <reference path="../../../node_modules/@ms/office-online-strings/en-US/transcription-strings.d.ts" />

import Dispatcher from '../dispatcher';
import { IAuthenticationProvider } from '../../backend/interfaces/IAuthenticationProvider';
import * as AudioBackup from '../../backend/interfaces/IAudioBackup';
import { CreateDefaultAudioBackup } from '../../backend/audioBackup/AudioBackupFactory';
import { AudioRecoveryTask } from '../../backend/AudioRecoveryTask';
import { TaskListResult, TaskListResultStatus } from '../../backend/TaskList';
import { LogEvents } from '../../utils/LogEvents';
import { BootKeys } from '../telemetry/BootKeys';
import { ITranscriptionSourceFile } from '../../backend/interfaces/ITranscriptionSourceFileProvider';
import { IndexeddbAudioBackupDummy } from '../../backend/audioBackup/IndexeddbAudioBackupDummy';
import { LogEventNames } from "../../backend/interfaces/ITelemetryLogger";
import { LogError } from "../../utils/ErrorTelemetry";
import { MAX_INDEXED_DB_INITIALIZE_TIME_MILLISECONDS } from '../../AppConstants';
import { PromiseWithTimeout } from '../../utils/utils';

export const RecoveringActionTypes = {
  SetRecoveredTranscriptionFile: "RECOVERING_SET_RECOVERED_TRANSCRIPTION_FILE"
};

export function SetRecoveredTranscriptionFile(recoveredTranscriptionFile: ITranscriptionSourceFile) {
  Dispatcher.dispatch({
    type: RecoveringActionTypes.SetRecoveredTranscriptionFile,
    payload: recoveredTranscriptionFile
  });
}

export type AudioBackupAndRecoveryStatus = { audioBackup: AudioBackup.IAudioBackup, needsRecovery: boolean };

export async function InitializeAudioBackup(): Promise<AudioBackupAndRecoveryStatus> {
  // Calling the appropriate CreateDefaultAudioBackup function based upon  FG value.
  let audioBackup: AudioBackup.IAudioBackup = CreateDefaultAudioBackup();
  let needsRecovery: boolean = false;

  try {
    // Initialize audioBackup with timeout
    await PromiseWithTimeout(() => { return audioBackup.Initialize() }, MAX_INDEXED_DB_INITIALIZE_TIME_MILLISECONDS /*timeout*/, "audioBackup.Initialize()" /*promise name*/);
  } catch (e) {
    LogError(LogEventNames.Warning, "Failed to initialize indexeddb because " + e);
    audioBackup = new IndexeddbAudioBackupDummy(); // setting audioBackup to dummy indexeddb object
  }

  if (audioBackup.IsInitializedSuccessfully()) {
    let backupState: AudioBackup.BackupStates = await audioBackup.FetchBackupState();
    switch (backupState) {
      case AudioBackup.BackupStates.Invalid:
        LogEvents.Boot.AddData([{ key: BootKeys.AudioBackupState, value: 'Invalid' }]);
        await audioBackup.ClearBackup();

        // Initialize audioBackup with timeout

        await PromiseWithTimeout(() => { return audioBackup.Initialize() }, MAX_INDEXED_DB_INITIALIZE_TIME_MILLISECONDS /*timeout*/, "audioBackup.Initialize()" /*promise name*/);
        break;
      case AudioBackup.BackupStates.None:
        LogEvents.Boot.AddData([{ key: BootKeys.AudioBackupState, value: 'None' }]);
        break;
      case AudioBackup.BackupStates.Active:
        LogEvents.Boot.AddData([{ key: BootKeys.AudioBackupState, value: 'Active' }]);
        needsRecovery = true;
        break;
      default:
        throw new Error('Unexpected backup state: ' + backupState);
    }
  }

  return { audioBackup, needsRecovery };
}

export async function RecoverFromBackup(audioBackup: AudioBackup.IAudioBackup, authProvider: IAuthenticationProvider): Promise<ITranscriptionSourceFile | null> {
  if (audioBackup == null) {
    throw new Error("Audio backup not initialized");
  }

  // Upload the file from audio backup to OneDrive. Returns null if the operation was canceled.
  const audioRecoveryTask: AudioRecoveryTask = new AudioRecoveryTask(authProvider);
  const uploadResult: TaskListResult<ITranscriptionSourceFile> =
    await audioRecoveryTask.ExecuteAudioBackupRecoveryTask(audioBackup,
      TranscriptionStrings.FolderNameToUploadAudioFiles);

  // If the upload was canceled, we are done.
  if (uploadResult.status == TaskListResultStatus.Canceled) {
    return null;
  }

  if (uploadResult.result == null) {
    throw new Error("Recovery TaskList result was null");
  }

  return uploadResult.result;
}
